import { Link, usePage } from "@inertiajs/react"
import { MoreHorizontal } from "lucide-react"
import { useState } from "react"

import { useNavPermissions } from "@/hooks/use-nav-permissions"
import { findActiveIndex, iconNavPermissions, navItems } from "@/lib/nav-data"
import type { IconNavItem } from "@/lib/nav-data"

const PRIMARY_LABELS = ["Home", "Cases", "Calendar", "Activity"]

export function MobileTabBar() {
  const { url } = usePage()
  const currentPath = url.split("?")[0]
  const perms = useNavPermissions()
  const [moreOpen, setMoreOpen] = useState(false)

  const filteredItems = navItems.filter((item) => {
    const check = iconNavPermissions[item.label]
    if (check && !check(perms)) return false
    return true
  })

  const primaryItems = filteredItems.filter((item) =>
    PRIMARY_LABELS.includes(item.label),
  )
  const overflowItems = filteredItems.filter(
    (item) => !PRIMARY_LABELS.includes(item.label),
  )

  const activeIndex = findActiveIndex(filteredItems, currentPath)
  const activeItem = activeIndex >= 0 ? filteredItems[activeIndex] : null
  const isOverflowActive =
    activeItem != null && !PRIMARY_LABELS.includes(activeItem.label)

  return (
    <>
      {/* More overlay */}
      {moreOpen && (
        <>
          <div
            className="fixed inset-0 z-30"
            onClick={() => setMoreOpen(false)}
          />
          <div className="fixed right-3 bottom-16 left-3 z-40 rounded-xl border border-gray-200 bg-white shadow-xl">
            <div className="grid grid-cols-4 gap-1 p-3">
              {overflowItems.map((item) => {
                const Icon = item.icon
                const active = activeItem?.label === item.label
                return (
                  <Link
                    key={item.label}
                    href={item.href}
                    prefetch
                    onClick={() => setMoreOpen(false)}
                    className={`flex flex-col items-center gap-1 rounded-lg px-1 py-2.5 text-center text-[10px] leading-tight transition-colors ${
                      active
                        ? "text-hpi-active-blue bg-hpi-pale-blue font-medium"
                        : "text-hpi-dark-gray hover:bg-gray-100"
                    }`}
                  >
                    <Icon className="h-5 w-5" strokeWidth={active ? 2 : 1.5} />
                    <span>{item.label}</span>
                  </Link>
                )
              })}
            </div>
          </div>
        </>
      )}

      {/* Tab bar */}
      <nav className="border-hpi-light-gray fixed inset-x-0 bottom-0 z-40 flex border-t bg-white pb-[env(safe-area-inset-bottom)]">
        {primaryItems.map((item) => (
          <TabItem
            key={item.label}
            item={item}
            active={activeItem?.label === item.label}
          />
        ))}

        {/* More button */}
        {overflowItems.length > 0 && (
          <button
            onClick={() => setMoreOpen(!moreOpen)}
            className={`flex flex-1 flex-col items-center gap-0.5 pt-2 pb-1.5 text-[10px] leading-tight transition-colors ${
              isOverflowActive || moreOpen
                ? "text-hpi-active-blue font-medium"
                : "text-hpi-dark-gray"
            }`}
          >
            <MoreHorizontal
              className="h-5 w-5"
              strokeWidth={isOverflowActive || moreOpen ? 2 : 1.5}
            />
            <span>More</span>
          </button>
        )}
      </nav>
    </>
  )
}

function TabItem({ item, active }: { item: IconNavItem; active: boolean }) {
  const Icon = item.icon
  return (
    <Link
      href={item.href}
      prefetch
      className={`flex flex-1 flex-col items-center gap-0.5 pt-2 pb-1.5 text-[10px] leading-tight transition-colors ${
        active ? "text-hpi-active-blue font-medium" : "text-hpi-dark-gray"
      }`}
    >
      <Icon className="h-5 w-5" strokeWidth={active ? 2 : 1.5} />
      <span>{item.label}</span>
    </Link>
  )
}
